import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import { Button } from "../../@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";

function normalizeAssignedRoles(user) {
  const raw = user?.role;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r) => ({
      roleId: r?.roleId ?? r?.role_id ?? r?.id,
      roleTitle: r?.roleTitle ?? r?.role_title ?? r?.title ?? r?.role,
    }))
    .filter((r) => r.roleId != null && String(r.roleTitle || "").trim().length);
}



export default function EditUserRolesDialog({
  user,
  open,
  onOpenChange,
  onUpdated,
}) {
  const { getToken } = useAuth();
  const [allRoles, setAllRoles] = useState([]);
  const [assignedRoles, setAssignedRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAssignedRoles(normalizeAssignedRoles(user));
    setSelectedRoleId("");
  }, [open, user]);

  const resetState = () => {
    setAssignedRoles(normalizeAssignedRoles(user));
    setSelectedRoleId("");
  };

  const fetchRoles = async () => {
    setLoadingRoles(true);
    try {
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.get(api_endpoint + "/user/roles", reqConfig);
      const roles = response.data?.roles || [];
      const formatted = roles
        .map((r) => ({
          id: r?.id,
          title: r?.title,
        }))
        .filter((r) => r.id != null && String(r.title || "").trim().length);

      setAllRoles(formatted);
    } catch (error) {
      console.error("Failed to fetch roles", error);
      toast.error("Failed to load roles.");
      setAllRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  useEffect(() => {
    if (open) fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const assignedRoleIdSet = useMemo(() => {
    return new Set(assignedRoles.map((r) => String(r.roleId)));
  }, [assignedRoles]);

  const addableRoles = useMemo(() => {
    return (allRoles || []).filter((r) => !assignedRoleIdSet.has(String(r.id)));
  }, [allRoles, assignedRoleIdSet]);

  const removeRoleFromList = (roleId) => {
    setAssignedRoles((prev) =>
      prev.filter((r) => String(r.roleId) !== String(roleId))
    );
  };

  const addRoleToList = (roleId) => {
    const meta = (allRoles || []).find((r) => String(r.id) === String(roleId));
    const roleTitle = meta?.title || "Role";
    setAssignedRoles((prev) => {
      const exists = prev.some((r) => String(r.roleId) === String(roleId));
      if (exists) return prev;
      return [...prev, { roleId, roleTitle }];
    });
  };

  const handleSave = async () => {
    if (!user?.id) {
      toast.error("No user selected.");
      return;
    }

    const roleIds = (assignedRoles || [])
      .map((r) => Number(r.roleId))
      .filter((n) => Number.isFinite(n));

    setSaving(true);
    try {
      const token = await getToken();
      if (!token) return;

      console.log({ user_id: user?.id, role_ids: roleIds, assignedRoles });

      await axios.put(
        api_endpoint + "/user/users/roles/edit",
        {
          user_id: user.id,
          role_ids: roleIds,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Roles updated.");
      await Promise.resolve(
        onUpdated?.({
          userId: user.id,
          roleIds,
          assignedRoles,
        })
      );
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to save roles", error);
      toast.error("Failed to save roles. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const isBusy = loadingRoles || saving;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg h-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-(--neutral-900)">
            Edit User Roles
          </DialogTitle>
          <DialogDescription className="font-body text-(--neutral-700)">
            Manage roles for{" "}
            <span className="font-semibold">
              {user?.name || user?.username || user?.email || "this user"}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="font-body text-sm text-(--neutral-700)">
              Add role
            </div>
            <div className="flex flex-col gap-2">
              <Select
                value={selectedRoleId}
                onValueChange={(next) => {
                  setSelectedRoleId(next);
                  if (!next) return;
                  addRoleToList(next);
                  setSelectedRoleId("");
                }}
              >
                <SelectTrigger className="w-full sm:flex-1">
                  <SelectValue
                    placeholder={loadingRoles ? "Loading roles..." : "Select a role"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {addableRoles.map((role) => (
                    <SelectItem key={String(role.id)} value={String(role.id)}>
                      {role.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <div className="font-body text-sm text-(--neutral-700)">
              Assigned roles
            </div>
            {assignedRoles.length === 0 ? (
              <div className="mt-2 text-sm font-body text-(--neutral-500)">
                No roles assigned.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 mt-2">
                {assignedRoles.map((r) => (
                  <div key={String(r.roleId)} className="tag-chip">
                    <span className="font-body">{r.roleTitle}</span>
                    <button
                      type="button"
                      onClick={() => removeRoleFromList(r.roleId)}
                      className="chip-remove"
                      disabled={isBusy}
                      aria-label={`Remove ${r.roleTitle}`}
                      title={`Remove ${r.roleTitle}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button
                variant="outline"
                disabled={isBusy}
                onClick={() => {
                  resetState();
                }}
              >
                Cancel
              </Button>
            </DialogClose>

            <Button type="button" variant="" disabled={isBusy} onClick={handleSave}>
              {saving ? <ClipLoader size={18} color="#fff" /> : "Save"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
